import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { useGuestStore } from '../stores/guestStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useVoiceStore } from '../stores/voiceStore'
import { Mic, Users, Clock, Shield } from 'lucide-react'
import PreJoinDeviceCheck from '../components/voice/PreJoinDeviceCheck'

export default function GuestJoinPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [invite, setInvite] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [joining, setJoining] = useState(false)
  // When logged-in users click "Switch account" we drop them into the
  // guest flow without auto-bouncing through /login. Defaults to
  // showing-as-authed when the user has an account; falls back to the
  // pre-existing "I have an account" toggle for anonymous visitors.
  const [forceGuestFlow, setForceGuestFlow] = useState(false)
  const [hasAccount, setHasAccount] = useState(false)

  useEffect(() => {
    if (!code) return
    api.get(`/channel-invites/${code}`)
      .then((res: any) => setInvite(res.data))
      .catch((err: any) => setError(err.response?.data?.error || 'Invalid invite'))
      .finally(() => setLoading(false))
  }, [code])

  const handleJoin = async () => {
    if (!code) return
    setJoining(true)
    try {
      const res = await api.post(`/channel-invites/${code}/join`, {
        displayName: displayName.trim() || 'Guest'
      })
      const data = res.data
      // OSPROD-140: invite generated from a voice room should drop the
      // invitee straight into that room, not into the server's channel
      // list. We piggy-back on OSPROD-104's auto-join intent — set it
      // BEFORE navigate so VoiceChannelView consumes it on mount and
      // fires the normal join flow. Text channels skip this (no join).
      const isVoice = invite?.channel?.type === 'VOICE'
      if (isVoice && data.channelId) {
        useVoiceStore.getState().setPendingAutoJoin(data.channelId)
      }
      if (data.type === 'guest') {
        useGuestStore.getState().setGuest({
          guestToken: data.guestToken,
          displayName: data.displayName,
          channelId: data.channelId,
          serverId: data.serverId,
          // OSPROD-160: carry the invited channel's name + type so the
          // guest sees the right room and lands in it even though the
          // guest's scoped serverStore doesn't carry the channel row.
          channelName: invite?.channel?.name,
          channelType: invite?.channel?.type,
        })
        navigate(`/servers/${data.serverId}/channels/${data.channelId}?guest=1`)
      } else {
        // OSPROD-129: backend created the ServerMember row, but the
        // sidebar's server list is still cached from login. Refetch
        // BEFORE we navigate so the newly-joined server (and its
        // channels — including voice rooms) shows up immediately. Old
        // behavior was: navigate, sidebar stays stale, user can read
        // messages in the URL'd channel but the server doesn't appear
        // in the rail and they can't reach any other channel / join
        // the active call without a full page reload.
        try {
          await useServerStore.getState().fetchServers()
        } catch {
          // Refetch failure is non-fatal — navigate anyway; the user
          // will at least see the destination channel even if the rail
          // is stale for a moment.
        }
        // OSPROD-89 retest: personal meeting rooms live on the hidden
        // `_personal_rooms` server (OSPROD-58) which never appears in
        // the rail list. The regular workspace route can't host it —
        // ChannelList's stale-URL self-cleanup sees an unknown serverId
        // and bounces to '/', dropping the user into the first Rx2Go
        // TEXT channel instead of the voice room. Mark those joins as
        // standalone meetings so Chat renders the voice surface without
        // the server sidebar.
        const inRail = useServerStore.getState().servers.some((s) => s.id === data.serverId)
        navigate(`/servers/${data.serverId}/channels/${data.channelId}${inRail ? '' : '?meeting=1'}`)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to join')
    } finally {
      setJoining(false)
    }
  }

  const handleLoginRedirect = () => {
    navigate(`/login?redirect=/join/${code}`)
  }

  const handleRegisterRedirect = () => {
    navigate(`/register?redirect=/join/${code}`)
  }

  if (loading) {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center">
        <div className="bg-card rounded-xl p-8 max-w-md text-center border border-border">
          <div className="text-destructive text-lg font-semibold mb-2">{error}</div>
          <p className="text-muted-foreground text-sm">This invite link is no longer valid.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen w-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card rounded-xl p-8 max-w-md w-full border border-border">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-foreground mb-1">{invite.server.name}</h1>
          <p className="text-muted-foreground text-sm">
            You are invited to join <span className="text-foreground font-medium">#{invite.channel.name}</span>
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {invite.channel.type === 'VOICE' ? <Mic size={14} /> : <Users size={14} />}
            {invite.channel.type === 'VOICE' ? 'Voice channel' : 'Text channel'}
          </div>
          {invite.expiresAt && (
            <div className="flex items-center gap-1.5 text-xs text-yellow-500">
              <Clock size={14} />
              Expires {new Date(invite.expiresAt).toLocaleString()}
            </div>
          )}
        </div>

        {invite.channel.type === 'VOICE' && <PreJoinDeviceCheck />}

        {isAuthenticated && currentUser && !forceGuestFlow ? (
          // Already-signed-in path — one-click join with the user's
          // existing account. Without this branch the page would
          // unconditionally render the guest UI; clicking "I have an
          // account → Log In" then bounced through /login (already
          // authed → Navigate to "/") which RootLanding resolved to
          // the user's last Business-OS chat — exactly the bug
          // reported as "invite link drops me into a previous chat".
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Signed in as <span className="text-foreground font-medium">{currentUser.username}</span>
            </p>
            <button
              onClick={handleJoin}
              disabled={joining}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition disabled:opacity-50"
            >
              {joining ? 'Joining…' : `Join as ${currentUser.username}`}
            </button>
            {invite.allowGuest && (
              <button
                onClick={() => setForceGuestFlow(true)}
                className="w-full text-muted-foreground text-sm hover:text-foreground transition"
              >
                Join as guest instead
              </button>
            )}
          </div>
        ) : hasAccount ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Sign in to join as your account
            </p>
            <button
              onClick={handleLoginRedirect}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition"
            >
              Log In
            </button>
            <button
              onClick={() => setHasAccount(false)}
              className="w-full text-muted-foreground text-sm hover:text-foreground transition"
            >
              Join as guest instead
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {!invite.allowGuest ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-500 text-center">
                <Shield size={16} className="inline mr-1 mb-0.5" />
                This invite requires an account
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Your display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                  className="w-full bg-card text-foreground text-sm rounded px-3 py-2.5 border border-border focus:border-primary outline-none"
                />
                <button
                  onClick={handleJoin}
                  disabled={joining || !displayName.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition disabled:opacity-50"
                >
                  {joining ? 'Joining...' : 'Join as Guest'}
                </button>
                <div className="text-xs text-muted-foreground text-center">
                  Guest access expires in 1 hour
                </div>
              </>
            )}
            <button
              onClick={() => setHasAccount(true)}
              className="w-full text-muted-foreground text-sm hover:text-foreground transition"
            >
              I have an account
            </button>
            <button
              onClick={handleRegisterRedirect}
              className="w-full text-muted-foreground text-xs hover:text-foreground transition"
            >
              Sign up for a permanent account
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
